import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { queryString, request } from "../api";
import { EmptyState, ErrorState, JSONPreview, LoadingState, PageHeader, Panel } from "../components";
import { formatTime } from "../format";
import type { AuditEvent } from "../types";

export function AuditPage() {
  const [tenant, setTenant] = useState("");
  const [search, setSearch] = useState("");
  const audit = useQuery({
    queryKey: ["audit", tenant],
    queryFn: () => request<AuditEvent[]>(`/data/audit${queryString({ tenant_id: tenant, limit: 200 })}`),
    refetchInterval: 30_000,
  });
  const visible = useMemo(() => audit.data?.filter((event) => JSON.stringify(event).toLowerCase().includes(search.toLowerCase())) ?? [], [audit.data, search]);

  return (
    <>
      <PageHeader eyebrow="Immutable audit trail" title="Who changed what, where, and why" description="Control actions and sensitive diagnostic reveals are attributed to an operator and request context." />
      <Panel>
        <div className="filter-bar">
          <label className="search-field"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search action, actor, reason, or entity" /></label>
          <label className="compact-field"><span>Tenant ID</span><input value={tenant} onChange={(event) => setTenant(event.target.value)} placeholder="All tenants" /></label>
        </div>
        {audit.isLoading && <LoadingState />}
        {audit.error && <ErrorState error={audit.error} />}
        {visible.length > 0 ? (
          <div className="audit-timeline">
            {visible.map((event) => (
              <details key={event.ID}>
                <summary>
                  <span className="audit-timeline__node" />
                  <div><strong>{event.Action}</strong><small>{event.ActorRole ?? "system"} · {event.ActorID ?? "Nexus runtime"}</small></div>
                  <code>{event.EntityType ?? "system"} / {event.EntityID ?? "global"}</code>
                  <time>{formatTime(event.CreatedAt)}</time>
                </summary>
                <div className="audit-timeline__detail">
                  <p><strong>Reason:</strong> {event.Reason ?? "Automated engine action"}</p>
                  <JSONPreview value={event} />
                </div>
              </details>
            ))}
          </div>
        ) : !audit.isLoading && <EmptyState title="No audit events match" body="Operator mutations and raw-data reveals will appear here." />}
      </Panel>
    </>
  );
}
